import { useState } from 'react';
import type { RecordedSet, SessionData } from '../types';
import type { SessionTimer } from '../hooks/useSessionTimer';
import { formatTime } from '../utils';

interface RoundReviewPhaseProps {
  session: SessionData;
  completedRound: number;
  recordedSets: RecordedSet[];
  timer: SessionTimer;
  onSkipRest: () => void;
  onStartNextRound: (adjustments: Map<string, { reps: number; weight: number }>) => void;
}

export function RoundReviewPhase({
  session,
  completedRound,
  recordedSets,
  timer,
  onSkipRest,
  onStartNextRound,
}: RoundReviewPhaseProps) {
  const roundSets = recordedSets.filter((s) => s.roundNumber === completedRound);

  const [adjustments, setAdjustments] = useState<Map<string, { reps: number; weight: number }>>(() => {
    const map = new Map<string, { reps: number; weight: number }>();
    for (const ex of session.exercises) {
      const set = roundSets.find((s) => s.sessionExerciseId === ex.id && s.side !== 'right');
      map.set(ex.id, {
        reps: set?.repsCompleted ?? ex.targetReps ?? 0,
        weight: set?.weightUsedKg ?? ex.suggestedWeightKg ?? 0,
      });
    }
    return map;
  });

  const adjustReps = (exerciseId: string, delta: number) => {
    setAdjustments((prev) => {
      const next = new Map(prev);
      const current = next.get(exerciseId)!;
      next.set(exerciseId, { ...current, reps: Math.max(0, current.reps + delta) });
      return next;
    });
  };

  const adjustWeight = (exerciseId: string, delta: number) => {
    setAdjustments((prev) => {
      const next = new Map(prev);
      const current = next.get(exerciseId)!;
      next.set(exerciseId, { ...current, weight: Math.max(0, current.weight + delta) });
      return next;
    });
  };

  return (
    <div className="flex flex-col items-center gap-6 p-6">
      <h2
        className="text-heading-xl text-gray-900 dark:text-textDark-primary"
        data-testid="round-review-title"
      >
        Round {completedRound} Complete
      </h2>

      {/* Rest countdown timer */}
      {timer.isRunning && (
        <div className="flex flex-col items-center gap-2" data-testid="round-review-timer">
          <span className="text-meta text-gray-500 dark:text-textDark-muted">
            Rest before next round
          </span>
          <span
            className="text-5xl font-mono font-bold tabular-nums text-brand-600 dark:text-brand-400"
            data-testid="round-review-timer-countdown"
          >
            {formatTime(timer.timeRemaining)}
          </span>
          <div className="h-1.5 w-full max-w-lg overflow-hidden rounded-full bg-gray-200 dark:bg-dark-border">
            <div
              className="h-full rounded-full bg-brand-600 dark:bg-brand-400"
              style={{ width: `${(1 - timer.progress) * 100}%` }}
              data-testid="round-review-timer-progress"
            />
          </div>
          <button
            onClick={onSkipRest}
            className="mt-1 text-sm font-medium text-gray-500 hover:text-gray-700 dark:text-textDark-muted dark:hover:text-textDark-secondary transition-colors"
            data-testid="round-review-skip-rest-button"
          >
            Skip Rest
          </button>
        </div>
      )}

      <div className="w-full max-w-lg space-y-3">
        {session.exercises.map((ex, index) => {
          const adj = adjustments.get(ex.id)!;
          return (
            <div
              key={ex.id}
              className="rounded-lg border border-gray-200 bg-white p-4 dark:border-dark-border dark:bg-dark-surface"
              data-testid={`round-review-exercise-${index}`}
            >
              <p className="mb-2 text-body font-medium text-gray-900 dark:text-textDark-primary">
                {ex.exerciseName}
              </p>

              {/* Weight adjustment */}
              <div className="mb-2 flex items-center justify-between">
                <span className="text-meta text-gray-500 dark:text-textDark-muted">Weight</span>
                <div
                  className="flex items-center gap-2"
                  data-testid={`round-review-adjust-weight-${index}`}
                >
                  <button
                    onClick={() => adjustWeight(ex.id, -2)}
                    className="flex h-8 w-8 items-center justify-center rounded border border-gray-300 text-sm font-bold text-gray-700 hover:bg-gray-100 dark:border-dark-border dark:text-textDark-secondary dark:hover:bg-dark-elevated"
                    aria-label="Decrease weight"
                  >
                    −
                  </button>
                  <span className="min-w-[3rem] text-center text-body text-gray-900 dark:text-textDark-primary">
                    {adj.weight} kg
                  </span>
                  <button
                    onClick={() => adjustWeight(ex.id, 2)}
                    className="flex h-8 w-8 items-center justify-center rounded border border-gray-300 text-sm font-bold text-gray-700 hover:bg-gray-100 dark:border-dark-border dark:text-textDark-secondary dark:hover:bg-dark-elevated"
                    aria-label="Increase weight"
                  >
                    +
                  </button>
                </div>
              </div>

              {/* Reps adjustment */}
              {ex.exerciseMode === 'RepBased' && (
                <div className="flex items-center justify-between">
                  <span className="text-meta text-gray-500 dark:text-textDark-muted">Reps</span>
                  <div
                    className="flex items-center gap-2"
                    data-testid={`round-review-adjust-reps-${index}`}
                  >
                    <button
                      onClick={() => adjustReps(ex.id, -1)}
                      className="flex h-8 w-8 items-center justify-center rounded border border-gray-300 text-sm font-bold text-gray-700 hover:bg-gray-100 dark:border-dark-border dark:text-textDark-secondary dark:hover:bg-dark-elevated"
                      aria-label="Decrease reps"
                    >
                      −
                    </button>
                    <span className="min-w-[3rem] text-center text-body text-gray-900 dark:text-textDark-primary">
                      {adj.reps}
                    </span>
                    <button
                      onClick={() => adjustReps(ex.id, 1)}
                      className="flex h-8 w-8 items-center justify-center rounded border border-gray-300 text-sm font-bold text-gray-700 hover:bg-gray-100 dark:border-dark-border dark:text-textDark-secondary dark:hover:bg-dark-elevated"
                      aria-label="Increase reps"
                    >
                      +
                    </button>
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>

      <button
        onClick={() => onStartNextRound(adjustments)}
        className="w-full max-w-lg rounded-md bg-brand-600 px-6 py-4 text-lg font-semibold text-white transition-colors hover:bg-brand-700 active:bg-brand-800"
        data-testid="round-review-start-next-button"
      >
        Start Round {completedRound + 1}
      </button>
    </div>
  );
}
